import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  type?: string;
  noindex?: boolean;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogImage = 'https://storage.googleapis.com/gpt-engineer-file-uploads/XvGEHuHQKWMZ0WYijg2RzW6QEI33/social-images/social-1762405247632-Untitled%20design%20(1).jpg',
  type = 'website',
  noindex = false,
}: SEOProps) => {
  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, property?: boolean) => {
      const attribute = property ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Standard meta tags
    updateMetaTag('description', description);
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }
    
    // Robots meta tag for noindex
    if (noindex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      // Remove noindex if it was previously set
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta && robotsMeta.getAttribute('content') === 'noindex, nofollow') {
        robotsMeta.setAttribute('content', 'index, follow');
      }
    }

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:image', ogImage, true);
    
    if (canonical) {
      updateMetaTag('og:url', canonical, true);
    }

    // Twitter tags
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', ogImage);

    // Update canonical link
    if (canonical) {
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      
      canonicalLink.setAttribute('href', canonical);
    }

    // Add hreflang tags for international targeting
    const currentUrl = canonical || window.location.href;
    const hreflangs = [
      { lang: 'en-US', href: currentUrl },
      { lang: 'en-AU', href: currentUrl },
      { lang: 'en-GB', href: currentUrl },
      { lang: 'en', href: currentUrl }, // General English for Europe
      { lang: 'x-default', href: currentUrl }
    ];

    hreflangs.forEach(({ lang, href }) => {
      let hreflangLink = document.querySelector(`link[rel="alternate"][hreflang="${lang}"]`);
      
      if (!hreflangLink) {
        hreflangLink = document.createElement('link');
        hreflangLink.setAttribute('rel', 'alternate');
        hreflangLink.setAttribute('hreflang', lang);
        document.head.appendChild(hreflangLink);
      }
      
      hreflangLink.setAttribute('href', href);
    });
  }, [title, description, keywords, canonical, ogImage, type, noindex]);
};
